import { useCallback } from 'react';

import { useDeletePeer } from '@givebacks/api/store';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, Group, Modal, Stack, Text, notifications } from 'ui';

import { useCrowdfundingProvider } from '../../../../../../Context';
import { usePeerName } from '../../../hooks/usePeerName';

type RemovePeerModalProps = {
  uuid?: string;
  name?: string;
  opened: boolean;
  onClose: () => void;
};

const RemovePeerModalFooter = ({ uuid, onClose }: Partial<RemovePeerModalProps>) => {
  const { campaignId, causeId } = useCrowdfundingProvider();

  const { peer_type, peerName } = usePeerName();
  const { mutate: deletePeer } = useDeletePeer();
  const handleDeletePeer = useCallback(() => {
    if (!uuid || !peer_type || !causeId || !campaignId) return;
    deletePeer(
      { peerId: uuid, campaignId, causeId, peerType: peer_type },
      {
        onSuccess() {
          notifications.show({ message: `${peerName.singular} deleted!`, variant: 'success' });
          onClose?.();
        },
        onError(e) {
          notifications.show({
            message: `${getErrorMessage_v2(e, `${peerName.singular} could not be deleted`)}`,
            variant: 'error',
            autoClose: 8000,
          });
        },
      }
    );
  }, [uuid, peer_type, causeId, campaignId, deletePeer, peerName.singular, onClose]);
  return (
    <Group justify="flex-end">
      <ButtonMantine size="md" variant="subtle" onClick={onClose}>
        Cancel
      </ButtonMantine>
      <ButtonMantine size="md" color="cerise" onClick={handleDeletePeer}>
        Remove {peerName.singular}
      </ButtonMantine>
    </Group>
  );
};

const RemovePeerModal = ({ uuid, name, opened, onClose }: RemovePeerModalProps) => {
  const { peerName } = usePeerName();

  return (
    <Modal opened={opened} onClose={onClose} title={`Remove ${peerName.singular}`}>
      <Stack gap="lg">
        <Text fw={600}>Are you sure you want to remove {name}? </Text>
        <RemovePeerModalFooter uuid={uuid} onClose={onClose} />
      </Stack>
    </Modal>
  );
};

export { RemovePeerModal };
